import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import Loading from "../common/Loading";
import { toast } from "react-toastify";
import { getAd, getAdInsights, pauseAd, resumeAd } from "../../hook/useAdsApi";
import {
  AD_STATUS,
  AD_OBJECTIVES,
  statusBadgeClasses,
  statusCardClasses,
  getAdDisplayLabel,
  daysBetween,
  formatSubmittedAt,
} from "./adsConstants";
import AdInsightsPanel from "./AdInsightsPanel";
import AdStatusTimeline from "./AdStatusTimeline";
import badgeInReviewIcon from "../../assets/images/icons/ads-badge-in-review.svg";
import timelineDoneIcon from "../../assets/images/icons/ads-timeline-done.svg";
import timelinePendingIcon from "../../assets/images/icons/ads-timeline-pending.svg";
import closeCircleIcon from "../../assets/images/icons/ads-close-circle.svg";

const STATUS_MESSAGES = {
  [AD_STATUS.SUBMITTED]:
    "Your advertisement has been submitted and is currently being reviewed by the admin. We will notify you once it's approved.",
  [AD_STATUS.APPROVED]: "Your ad has been approved and will go live shortly.",
  [AD_STATUS.ACTIVE]: "Your ad is live and currently running.",
  [AD_STATUS.PAUSED]: "Your ad is currently paused.",
};

function statusIcon(status) {
  switch (status) {
    case AD_STATUS.REJECTED:
      return closeCircleIcon;
    case AD_STATUS.APPROVED:
    case AD_STATUS.ACTIVE:
      return timelineDoneIcon;
    case AD_STATUS.SUBMITTED:
      return badgeInReviewIcon;
    default:
      return timelinePendingIcon;
  }
}

export default function ViewAd() {
  const { adId } = useParams();
  const navigate = useNavigate();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const loadInsights = useCallback((params) => getAdInsights(adId, params), [adId]);

  const loadAd = () => {
    setLoading(true);
    getAd(adId)
      .then(setAd)
      .catch(() => toast.error("Failed to load ad", { toastId: "ad-load-error" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId]);

  const handlePauseResume = async () => {
    setActionLoading(true);
    try {
      if (ad.status === AD_STATUS.ACTIVE) {
        await pauseAd(adId);
        toast.success("Ad paused");
      } else {
        await resumeAd(adId);
        toast.success("Ad resumed");
      }
      loadAd();
    } catch {
      toast.error("Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const coverImage = ad?.creative_media?.[0]?.url;
  const objective = AD_OBJECTIVES.find((o) => o.value === ad?.campaign?.objective);
  const duration = ad ? daysBetween(ad.campaign?.start_date, ad.campaign?.end_date) : null;
  const dailyBudget = ad?.campaign?.budget && duration ? (Number(ad.campaign.budget) / duration).toFixed(2) : null;
  // The page layout is the same regardless of status - the only thing that changes is that
  // Approved/Active/Paused additionally get the performance report, stacked below the rest.
  const showCharts =
    ad && (ad.status === AD_STATUS.APPROVED || ad.status === AD_STATUS.ACTIVE || ad.status === AD_STATUS.PAUSED);
  // submitAd() sets both status and updated_at at the moment of submission, so updated_at
  // is an accurate "submitted at" timestamp for a freshly-submitted ad (it will drift if the
  // ad is later edited/resubmitted, since there's no separate per-transition timestamp stored).
  const submittedAt = ad && ad.status !== AD_STATUS.DRAFT ? formatSubmittedAt(ad.updated_at) : null;

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => navigate(-1)} className="rounded-full bg-[#e7f3ea] p-2">
                ←
              </button>
              <h1 className="text-xl font-bold">View Ad : ID: AD-{adId}</h1>
            </div>
            <div className="w-full h-1 bg-[#d9d9d9] rounded-full mb-6" />

            {loading || !ad ? (
              <div className="flex justify-center mt-16">
                <Loading />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <div className={`rounded-2xl border p-4 ${statusCardClasses(ad.status)}`}>
                  {coverImage && (
                    ad.creative_media?.[0]?.media_type === "video" ? (
                      <video src={coverImage} controls className="w-full h-40 object-cover rounded-xl mb-3" />
                    ) : (
                      <img src={coverImage} alt="" className="w-full h-40 object-cover rounded-xl mb-3" />
                    )
                  )}
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-full ${statusBadgeClasses(ad.status)}`}>
                      {getAdDisplayLabel(ad.status)}
                    </span>
                    <span className="text-xs text-[#747474]">ID: AD-{adId}</span>
                  </div>

                  <div className="flex items-center gap-2 mt-2">
                    <img src={statusIcon(ad.status)} alt="" className="w-6 h-6" />
                    <p className={`font-bold uppercase ${ad.status === AD_STATUS.REJECTED ? "text-red-600" : "text-[#9A1B2F]"}`}>
                      {getAdDisplayLabel(ad.status)}
                    </p>
                  </div>

                  {ad.status === AD_STATUS.REJECTED ? (
                    <p className="text-sm text-red-600 mt-2">{ad.rejection_reason || "Policy violation"}</p>
                  ) : (
                    STATUS_MESSAGES[ad.status] && (
                      <p className="text-sm text-[#434656] mt-2">{STATUS_MESSAGES[ad.status]}</p>
                    )
                  )}

                  {submittedAt && (
                    <p className="text-xs text-[#9a9a9a] mt-3 pt-3 border-t">Submitted {submittedAt}</p>
                  )}
                </div>

                <div className="rounded-2xl border p-4">
                  {objective && (
                    <div className="flex items-center gap-3 mb-4">
                      {objective.iconSrc && <img src={objective.iconSrc} alt="" className="w-10 h-10" />}
                      <p className="font-semibold text-[#131b2e]">{objective.label}</p>
                    </div>
                  )}
                  <p className="text-sm font-semibold text-[#747474]">Headline</p>
                  <p className="text-[#131b2e] mb-3">{ad.headline || ad.name}</p>
                  <p className="text-sm font-semibold text-[#747474]">Description</p>
                  <p className="text-[#131b2e] mb-3">{ad.body_text}</p>
                  <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                    <div>
                      <p className="text-[#747474]">Daily budget</p>
                      <p className="font-semibold">$ {dailyBudget ?? "-"} / Day</p>
                    </div>
                    <div>
                      <p className="text-[#747474]">Schedule</p>
                      <p className="font-semibold">
                        {ad.campaign?.start_date} - {ad.campaign?.end_date}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-[#747474]">Estimated Total Spend</p>
                  <p className="font-semibold mb-1">
                    $ {ad.campaign?.budget ?? "-"} {duration ? `/ Over ${duration} Days` : ""}
                  </p>
                  {/* Nothing has happened yet for a Draft ad (not submitted, no review
                      history) - showing the timeline here would just be a checkmark with
                      everything else greyed out, so skip it until it's actually submitted. */}
                  {ad.status !== AD_STATUS.DRAFT && <AdStatusTimeline ad={ad} />}
                </div>

                {showCharts && <AdInsightsPanel load={loadInsights} showCharts />}

                <div className="flex flex-col gap-3">
                  {/* client-api rejects edits while an ad is Submitted for Review */}
                  <button
                    type="button"
                    disabled={ad.status === AD_STATUS.SUBMITTED}
                    onClick={() => navigate(`/ads/ads/${adId}/edit`)}
                    title={ad.status === AD_STATUS.SUBMITTED ? "Can't edit while under review" : undefined}
                    className="bg-[#095228] text-white font-semibold py-3 rounded-full disabled:opacity-50"
                  >
                    Edit & Resubmit
                  </button>
                  {(ad.status === AD_STATUS.ACTIVE || ad.status === AD_STATUS.PAUSED) && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={handlePauseResume}
                      className="border-2 border-[#095228] text-[#095228] font-semibold py-3 rounded-full disabled:opacity-50"
                    >
                      {ad.status === AD_STATUS.ACTIVE ? "Pause Ad" : "Resume Ad"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
